import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Lock } from 'lucide-react';
import { BoutonMint, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { PaveSignature } from '../ui/PaveSignature';
import type { SeanceAffichee } from '../../lib/useEmargement';
import { usePiegeFocus } from '../../lib/usePiegeFocus';

/**
 * Mode signature : la tablette quitte les mains du formateur.
 *
 * L'écran est volontairement pauvre. Un apprenant y voit son nom, sa formation,
 * la date, la demi-journée, un pavé pour signer, et rien d'autre — ni la liste
 * de ses camarades, ni leurs absences, ni le moyen de changer de session. La
 * seule sortie est gardée par le code posé à la validation de l'appel, et ce
 * code est vérifié **par l'API** : le contrôler dans le navigateur se
 * contournerait en rechargeant la page.
 *
 * Après chaque signature, l'écran enchaîne sur l'apprenant suivant. C'est ce qui
 * permet à la tablette de faire le tour de la salle sans revenir au formateur
 * entre deux personnes.
 */

export interface ApprenantASigner {
  presenceId: string;
  apprenantId: string;
  apprenant: string;
}

export function ModeSignature({
  seance,
  file,
  enCours,
  erreur,
  sortieEnCours,
  erreurSortie,
  onSigner,
  onSortir,
}: {
  seance: SeanceAffichee;
  /** Apprenants restant à faire signer, dans l'ordre de circulation. */
  file: ApprenantASigner[];
  enCours: boolean;
  erreur: string | null;
  sortieEnCours: boolean;
  erreurSortie: string | null;
  onSigner: (presenceId: string, trace: string) => void;
  onSortir: (pin: string) => void;
}): JSX.Element {
  const [trace, setTrace] = useState<string | null>(null);
  const [demandeSortie, setDemandeSortie] = useState(false);
  const [pin, setPin] = useState('');
  const [dernierSigne, setDernierSigne] = useState<string | null>(null);

  // Fenêtre plein écran (TF-341) : la tabulation ne sort pas vers l'écran de
  // suivi resté dessous, et `Échap` demande le code de sortie, comme le cadenas.
  const fenetre = useRef<HTMLDivElement>(null);
  usePiegeFocus(fenetre, true, () => setDemandeSortie(true));

  const courant = file[0] ?? null;
  const total = seance.compteurs.presents + seance.compteurs.retards;
  const signes = seance.compteurs.signatures;

  // Le pavé se vide entre deux apprenants : sans cela, le suivant hériterait du
  // tracé du précédent et pourrait le valider sans s'en apercevoir.
  useEffect(() => {
    setTrace(null);
  }, [courant?.presenceId]);

  const dateLisible = new Date(seance.date).toLocaleDateString('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div
      ref={fenetre}
      role="dialog"
      aria-modal="true"
      aria-labelledby="titre-mode-signature"
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-[#F4F1EB] focus:outline-none"
    >
      <header className="flex items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <p id="titre-mode-signature" className="font-display truncate text-sm font-bold text-codialis-navy">
            Émargement du {seance.demiJourneeLibelle.toLowerCase()} — {dateLisible}
          </p>
          <p className="truncate text-xs text-codialis-gray">{seance.libelle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="text-xs font-semibold text-codialis-navy">
            {signes} / {total} signé{signes > 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={() => setDemandeSortie(true)}
            aria-label="Quitter le mode signature"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-3 text-codialis-gray transition hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[48px]"
          >
            <Lock className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        {courant === null ? (
          <div className="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-2xl bg-white p-8 text-center shadow-card">
            <CheckCircle2 className="h-12 w-12 text-codialis-mint" aria-hidden="true" />
            <h2 className="font-display text-xl font-bold text-codialis-navy">
              Émargement du {seance.demiJourneeLibelle.toLowerCase()} terminé
            </h2>
            <p className="text-sm text-codialis-gray">
              {signes} signature{signes > 1 ? 's' : ''} recueillie
              {signes > 1 ? 's' : ''}. Rendez la tablette au formateur : le code de sortie est
              demandé pour revenir à l’écran de suivi.
            </p>
            <BoutonMint onClick={() => setDemandeSortie(true)}>Quitter le mode signature</BoutonMint>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-4">
            {dernierSigne && (
              <div className="flex items-center gap-2 rounded-2xl bg-codialis-sand px-4 py-3 text-sm text-codialis-navy">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-codialis-mint" aria-hidden="true" />
                <span>
                  <strong>{dernierSigne}</strong> — signé. Passez la tablette à l’apprenant
                  suivant.
                </span>
              </div>
            )}

            <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
              <h2 className="font-display text-2xl font-bold text-codialis-navy sm:text-3xl">
                {courant.apprenant}
              </h2>
              <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm text-codialis-gray sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="font-semibold">Formation</dt>
                  <dd className="min-w-0 truncate">{seance.formation ?? seance.libelle}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-semibold">Session</dt>
                  <dd className="min-w-0 truncate">{seance.session ?? '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-semibold">Date</dt>
                  <dd>{dateLisible}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-semibold">Période</dt>
                  <dd>
                    {seance.demiJourneeLibelle} · {seance.heureDebut} – {seance.heureFin}
                  </dd>
                </div>
              </dl>

              <div className="mt-4">
                {/* Grande zone : on signe au doigt, tablette tenue à la main,
                    et elle prend la hauteur disponible — en portrait, la carte
                    ne remplit pas l'écran et un grand pavé s'y signe mieux.

                    La `key` n'est pas décorative : sans elle, le pavé reste
                    monté d'un apprenant au suivant et son canvas garde le tracé
                    du précédent, si bien que la personne suivante signerait
                    par-dessus la signature de son camarade. Remettre l'état
                    React à zéro n'y suffit pas — c'est le bitmap qu'il faut
                    reconstruire. */}
                <PaveSignature
                  key={courant.presenceId}
                  onChanger={setTrace}
                  desactive={enCours}
                  hauteur="h-56 portrait:h-[40vh] landscape:h-56 lg:landscape:h-64"
                />
              </div>

              {erreur && (
                <div className="mt-3">
                  <MessageErreur message={erreur} />
                </div>
              )}

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <BoutonMint
                  onClick={() => {
                    if (!trace) return;
                    setDernierSigne(courant.apprenant);
                    onSigner(courant.presenceId, trace);
                  }}
                  disabled={!trace || enCours}
                >
                  {enCours ? 'Enregistrement…' : 'Valider ma signature'}
                </BoutonMint>
              </div>

              <p className="mt-3 text-[11px] text-codialis-gray">
                Votre signature, son horodatage et l’adresse du poste sont conservés comme preuve
                de votre présence sur cette demi-journée.
              </p>
            </div>

            {file.length > 1 && (
              <p className="text-center text-xs text-codialis-gray">
                {file.length - 1} apprenant{file.length - 1 > 1 ? 's' : ''} après celui-ci.
              </p>
            )}
          </div>
        )}
      </div>

      <Modale
        ouverte={demandeSortie}
        titre="Quitter le mode signature"
        zIndice="z-[60]"
        onFermer={() => {
          setDemandeSortie(false);
          setPin('');
        }}
      >
        <div className="space-y-3 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            Saisissez le code à quatre chiffres choisi à la validation de l’appel.
          </p>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            value={pin}
            onChange={(evenement) => setPin(evenement.target.value.replace(/\D/g, ''))}
            aria-label="Code de sortie"
            className="min-h-[56px] w-40 rounded-xl border-2 border-gray-200 text-center font-display text-2xl tracking-[0.5em] text-codialis-navy"
          />
          {erreurSortie && <MessageErreur message={erreurSortie} />}
          <div className="flex flex-wrap gap-2">
            <BoutonMint onClick={() => onSortir(pin)} disabled={pin.length !== 4 || sortieEnCours}>
              {sortieEnCours ? 'Vérification…' : 'Quitter'}
            </BoutonMint>
            <BoutonSecondaire
              onClick={() => {
                setDemandeSortie(false);
                setPin('');
              }}
            >
              Rester en mode signature
            </BoutonSecondaire>
          </div>
        </div>
      </Modale>
    </div>
  );
}
